import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, ElementRef, HostListener, inject, input, OnInit, output, ViewChild } from '@angular/core';
import { Customer, Document, Documents, Building, Mortgage, Application, DocumentType, DocumentClassification } from '@app/core/stingray-api/models';
import { BlobDownloadService } from '@app/core/blob-download.service';
import { DocumentsService } from '@app/core/stingray-api/services';
import { FileSystemDirectoryEntry, FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { finalize } from 'rxjs/operators';
import { FileDialogComponent, FileDialogData } from '../file-dialog/file-dialog.component';
import { MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { FilesService } from '../files.service';
import { GoogleAnalyticsService } from '@app/core/analytics/google-analytics.service';
import { FilesUploadProgress, FileUploadService } from '../file-upload.service';
import { ShellLoaderService } from '@app/shell/shell-loader.service';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatLine } from '@angular/material/core';
import { MatList, MatListItem } from '@angular/material/list';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent } from '@angular/material/card';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'unite-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    animations: [
        trigger('fadeAnimation', [
            // the "in" style determines the "resting" state of the element when it is visible.
            state('in', style({ opacity: 1 })),
            // fade in when created. this could also be written as transition('void => *')
            transition(':enter', [
                style({ opacity: 0 }),
                animate(750)
            ]),
            // fade out when destroyed. this could also be written as transition('void => *')
            transition(':leave', animate(1000, style({ opacity: 0 })))
        ])
    ],
    imports: [NgxFileDropModule, TranslateModule, MatButton, MatTooltip, MatIcon, MatCard, MatCardHeader, MatCardTitle, MatCardContent, MatList, MatListItem, MatLine, MatProgressBar, DecimalPipe, DatePipe]
})
export class FileUploadComponent implements OnInit {

  customer = input<Customer>();
  text = input<string>('');

  object = input<Building | Mortgage | Application>();
  objectType = input<'Building' | 'Mortgage' | 'Application' | 'Customer'>();

  documentTypes = input<DocumentType[]>();
  customers = input<Customer[]>();
  applications = input<Application[]>();
  mortgages = input<Mortgage[]>();
  buildings = input<Building[]>();

  newFile = output<'Building' | 'Mortgage' | 'Customer' | 'Other' | 'Application' | 'All'>();

  @ViewChild('fileUploadInput', { static: true }) fileUploadInput: ElementRef;
  showFullscreenDropzone = false;
  lastTarget: EventTarget;

  /**
   * An array with all the uploads currently being tracked
   */
  public uploads: FilesUploadProgress[] = [];

  /**
   * Wait this long (ms) after the last handled upload before refreshing the document lists
   */
  private static readonly RefreshDelay = 2000;

  /**
   * How long (ms) a document that needed no check stays in the list, so the user can see what it was indexed as
   */
  private static readonly AutoValidatedDisplayTime = 6000;
  private pendingRefresh = new Set<'Customer' | 'Building' | 'Mortgage' | 'Other'>();
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private destroyRef = inject(DestroyRef);
  private documentsService = inject(DocumentsService);

  constructor(private fileService: FilesService,
    private matDialog: MatDialog,
    private shellLoaderService: ShellLoaderService,
    private blobDownloadService: BlobDownloadService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private fileUploadService: FileUploadService
  ) {
    this.destroyRef.onDestroy(() => clearTimeout(this.refreshTimer));
  }

  ngOnInit() {
    this.loadDocumentsToValidate();
  }

  /**
   * Puts documents that were classified automatically but not checked yet (e.g. the page was refreshed before
   * clicking OK, or classified by the background job) back in the list, so they can still be validated
   */
  private loadDocumentsToValidate() {
    const customer = this.customer();
    if (!customer) {
      return;
    }

    const object = this.object();
    this.documentsService.getDocuments({
      customerId: customer.id,
      buildingId: this.objectType() === 'Building' ? object?.id : undefined,
      mortgageId: this.objectType() === 'Mortgage' ? object?.id : undefined,
      applicationId: this.objectType() === 'Application' ? object?.id : undefined,
      needsValidation: true,
      count: 50
    }).subscribe((documents: Documents) => {
      const uploads = this.fileUploadService.uploads;

      // The list is shared across pages: drop finished rows of other customers
      for (let index = uploads.length - 1; index >= 0; index--) {
        if (uploads[index].isComplete && !uploads[index].isAnalysing && uploads[index].customerId !== customer.id) {
          uploads.splice(index, 1);
        }
      }

      for (const document of documents.items ?? []) {
        if (uploads.some(upload => upload.document?.id === document.id)) {
          continue;
        }

        uploads.push(<FilesUploadProgress>{
          documentId: document.id,
          document: document,
          originalFileName: document.originalFileName,
          percentageComplete: 100,
          isComplete: true,
          customerId: customer.id,
          // Only the stored values survive a refresh; a suggestion that was not stored is shown as not recognised
          classification: {
            applied: !!document.documentType,
            document: document,
            classification: { documentTypeId: document.documentType?.id, confidence: document.classificationConfidence }
          }
        });
      }

      this.uploads = uploads;
    });
  }


  /**
   * Redirect the click event to the fileUploadInput
   * so the user can start selecting the file on his machine
   */
  uploadDocument() {
    this.fileUploadInput.nativeElement.click();
  }

  /**
   * Close the uploading card
   */
  closeUploading() {
    this.uploads = [];
  }

  isUploading(): boolean {
    return this.uploads.some(upload => !upload.isComplete && !upload.hasError);
  }


  @HostListener('window:dragenter', ['$event'])
  dragenterEnter(event: any) {
    this.lastTarget = event.target; // cache the last target here
    this.showDropzone();
  }

  @HostListener('window:dragleave', ['$event'])
  dragLeave(event: any) {
    if (event.target === this.lastTarget || event.target === document) {
      this.hideDropzone();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    // Analysing continues on the server, so only warn for uploads that are still in progress
    if (!this.uploads.every(upload => upload.isComplete)) {
      $event.returnValue = true;
    }
  }

  showDropzone() {
    const dropzone = document.getElementById('dropzone');
    if (dropzone) {
      dropzone.classList.add('show');
    }
  }

  hideDropzone() {
    const dropzone = document.getElementById('dropzone');
    if (dropzone) {
      dropzone.classList.add('hide');
      setTimeout(() => {
        dropzone.classList.remove('show');
        dropzone.classList.remove('hide');
      }, 300);
    }
  }

  /**
   * Bubbles the click event to the fileUploadInput
   * so the user can start selecting file on his machine
   */
  clickFileInput() {
    this.fileUploadInput.nativeElement.click();
  }

  /**
   * Triggered when the user drops a file into the designated box
   * @param event the event details of the ngx-file-drop
   */
  public fileDrop(files: NgxFileDropEntry[]) {

    this.googleAnalyticsService.trackEvent('document', 'upload', 'dragdrop');

    files.forEach(droppedFile => {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => { this.uploadFile(file); });
      } else {
        // It was a directory (empty directories are added, otherwise only files)
        const fileDirectory = droppedFile.fileEntry as FileSystemDirectoryEntry;
      }
    });

    this.hideDropzone();
  }

  uploadFile(file: File) {
    this.fileUploadService.uploadAndRegisterDocument(this.customer().id, file,
      () => this.uploadProgress(),
      (document) => this.documentRegistered(document),
      () => this.uploadProgress());
  }

  /**
   * Tries again after uploading or registering a file failed (e.g. a connection hiccup).
   * The file is still in memory, so there is no need to select it again.
   */
  retryUpload(fileUpload: FilesUploadProgress) {
    this.googleAnalyticsService.trackEvent('document', 'upload', 'retry');

    this.fileUploadService.retryUploadAndRegisterDocument(fileUpload,
      () => this.uploadProgress(),
      (document) => this.documentRegistered(document),
      () => this.uploadProgress());
  }

  /**
   * Drops a failed upload from the list
   */
  dismissUpload(fileUpload: FilesUploadProgress) {
    this.removeUpload(fileUpload, 0);
  }

  private uploadProgress() {
    this.uploads = this.fileUploadService.uploads;
  }

  /**
   * The file is stored and registered: the document type can be detected
   */
  private documentRegistered(document: Document) {
    this.uploads = this.fileUploadService.uploads;
    this.customer().documentsCount += 1;

    const fileUpload = this.uploads.find(upload => upload.document === document);
    if (fileUpload) {
      this.fileUploadService.classifyDocument(fileUpload, (classifiedUpload) => this.documentClassified(classifiedUpload));
    }
  }

  /**
   * The detected document type that was not confident enough to be stored, to be confirmed by the user
   */
  suggestedDocumentType(fileUpload: FilesUploadProgress): DocumentType | undefined {
    // Stored applications (exact lender reference) don't make the type stored: 'applied' alone says too little
    const classification = fileUpload.classification;
    if (!classification || fileUpload.document?.documentType) {
      return undefined;
    }

    return this.documentTypes()?.find(documentType => documentType.id === classification.classification?.documentTypeId);
  }

  /**
   * What the document was (or would be) linked to, e.g. an address, a shareholder or an application
   */
  linkText(fileUpload: FilesUploadProgress): string | undefined {
    const classification = fileUpload.classification;
    if (!classification) {
      return undefined;
    }

    // Stored links, completed with the suggested ones while the type is still a suggestion
    const document = fileUpload.document;
    const suggestion = document.documentType ? undefined : classification.classification;
    const customer = suggestion?.customerId ? this.customers()?.find(c => c.id === suggestion.customerId) : document.customer;
    const buildings = document.buildings?.length ? document.buildings : this.buildings()?.filter(b => suggestion?.buildingIds?.includes(b.id)) ?? [];
    const mortgages = document.mortgages?.length ? document.mortgages : this.mortgages()?.filter(m => suggestion?.mortgageIds?.includes(m.id)) ?? [];
    const applications = (document.applications ?? []).filter(a => a.id !== this.object()?.id);

    const parts: string[] = [];
    if (buildings.length) {
      parts.push(buildings.map(building => building.address?.addressLine1).join(', '));
    } else if (mortgages.length) {
      parts.push(mortgages.map(mortgage => (mortgage.number || '...') + ' - ' + mortgage.lender?.name).join(', '));
    } else if (customer && customer.id !== this.customer()?.id) {
      // Only worth mentioning when the document moved to e.g. a shareholder
      parts.push(customer.name);
    }
    if (applications.length) {
      parts.push(applications.map(application => application.name).join(', '));
    }

    return parts.length ? parts.join(' · ') : undefined;
  }

  /**
   * The detected document type (stored or suggested) can be accepted as is
   */
  canValidate(fileUpload: FilesUploadProgress): boolean {
    if (fileUpload.isAnalysing || fileUpload.isValidating || fileUpload.document?.validatedOn) {
      return false;
    }

    const documentType = this.detectedDocumentType(fileUpload);
    if (!documentType) {
      return false;
    }

    // On the page of a building/mortgage the document belongs to that object (as when categorizing it in the dialog)
    switch (this.objectType()) {
      case 'Application':
        return true; // any type can be linked to the application
      case 'Building':
      case 'Mortgage':
        return documentType.associationType === this.objectType();
      default:
        return true;
    }
  }

  /**
   * Accepts the detected document type, dates and links, and removes the upload from the list
   */
  validateDocument(fileUpload: FilesUploadProgress) {
    this.googleAnalyticsService.trackEvent('document', 'validate');

    const classification = fileUpload.classification;
    const documentType = this.detectedDocumentType(fileUpload);
    const isSuggestion = !fileUpload.document.documentType;
    const object = this.object();
    const objectLink = !!object && ['Building', 'Mortgage', 'Application'].includes(this.objectType());

    // A suggestion is stored on acceptance; a stored classification only needs the link to the page's object
    let accepted: DocumentClassification | undefined = isSuggestion ? { ...classification.classification } : undefined;
    if (objectLink) {
      accepted = { ...(accepted ?? {}), documentTypeId: documentType.id };
      switch (this.objectType()) {
        case 'Building':
          accepted.buildingIds = [object.id];
          break;
        case 'Mortgage':
          accepted.mortgageIds = [object.id];
          break;
        case 'Application':
          accepted.applicationIds = [...(accepted.applicationIds ?? []), object.id];
          break;
      }
    }

    // An already stored classification without new links only gets validated, so the document lists don't change
    const listsChange = isSuggestion || objectLink;

    this.fileUploadService.validateDocument(fileUpload, accepted, (validatedUpload) => {
      if (listsChange) {
        this.emitNewFile(validatedUpload.document.documentType);
      }
      this.removeUpload(validatedUpload);
    });
  }

  private detectedDocumentType(fileUpload: FilesUploadProgress): DocumentType | undefined {
    return fileUpload.document?.documentType ?? this.suggestedDocumentType(fileUpload);
  }

  /**
   * Refreshes the document lists when the document type was detected and stored on the document.
   * A document the API validated as well needs no check, so it leaves the list after a moment.
   */
  private documentClassified(fileUpload: FilesUploadProgress) {
    if (fileUpload.classification?.applied) {
      this.emitNewFile(fileUpload.document.documentType);
    }

    if (fileUpload.isAutoValidated) {
      this.removeUpload(fileUpload, FileUploadComponent.AutoValidatedDisplayTime);
    }
  }

  /**
   * Refreshes the document lists once the user pauses, instead of after every file,
   * so a list of uploads can be handled one by one without waiting for reloads
   */
  private emitNewFile(documentType?: DocumentType) {
    // Without a type (e.g. only linked to an application by its lender reference) the document is in 'Other'
    const newFileType = !documentType ? 'Other'
      : this.fileService.isCustomer(documentType.associationType) ? 'Customer' : documentType.associationType;
    this.pendingRefresh.add(<'Customer' | 'Building' | 'Mortgage' | 'Other'>newFileType);

    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.pendingRefresh.forEach(fileType => this.newFile.emit(fileType));
      this.pendingRefresh.clear();
    }, FileUploadComponent.RefreshDelay);
  }

  private removeUpload(fileUpload: FilesUploadProgress, delay = 500) {
    setTimeout(() => {
      const index = this.uploads.indexOf(fileUpload);
      if (index >= 0) {
        this.uploads.splice(index, 1);
      }
    }, delay);
  }


  public editDocument(fileUpload: FilesUploadProgress) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = false;
    dialogConfig.width = '520px';

    switch (this.objectType()) {
      case 'Building': {
        fileUpload.document.buildings.push(<Building>this.object());
        break;
      }
      case 'Mortgage': {
        fileUpload.document.mortgages.push(<Mortgage>this.object());
        break;
      }
      case 'Application': {
        fileUpload.document.applications.push(<Application>this.object());
        break;
      }
    }

    dialogConfig.data = <FileDialogData>{
      document: fileUpload.document,
      documentTypes: this.documentTypes(),
      customers: this.customers(),
      applications: this.applications(),
      mortgages: this.mortgages(),
      buildings: this.buildings(),
      objectType: this.objectType(),
      classification: fileUpload.classification
    };

    this.matDialog.open(FileDialogComponent, dialogConfig).afterClosed().subscribe((updatedDocument?: Document) => {
      // Deleted
      if (updatedDocument === null) {
        // Remove the file from the array
        setTimeout(() => {
          this.uploads.splice(this.uploads.indexOf(fileUpload), 1);
        }, 500);
      }

      // Updated
      if (updatedDocument) {
        this.emitNewFile(updatedDocument.documentType);

        // Remove the file from the array
        setTimeout(() => {
          this.uploads.splice(this.uploads.indexOf(fileUpload), 1);
        }, 500);
      }
    });
  }

  /**
   * Is triggered when the user select s a file through the form input button
   * @param event event details
   */
  fileChange(event: any) {
    this.googleAnalyticsService.trackEvent('document', 'upload', 'button');

    const fileList: FileList = event.target.files;
    for (let index = 0; index < fileList.length; index++) {
      const file = fileList[index];
      this.uploadFile(file);
    }
  }

  downloadDocument(document: Document) {
    this.googleAnalyticsService.trackEvent('document', 'download');
    this.shellLoaderService.setRequestLoader(true);
    const path = DocumentsService.DownloadDocumentPath.replace('{id}', document.id);
    this.blobDownloadService.download(path, undefined, document.originalFileName)
      .pipe(finalize(() => this.shellLoaderService.setRequestLoader(false))).subscribe();
  }

  fileOver() {
    this.showDropzone();
  }

  fileLeave() {
    this.hideDropzone();
  }
}

